/**
 * Grouping for "which calendars count as you being busy" lists: calendars
 * bucketed by the connector that owns them, labeled with the connector's own
 * name. Shared because Settings, the picker cards, and native's availability
 * screen all render this list, and the rules are subtle enough to drift the
 * moment there are two copies — `connectorId` is the grouping key
 * (`accountId` is the user's account and identical on every row), and a
 * primary calendar always leads its group.
 *
 * Typed structurally so web's and native's row/connector shapes both pass
 * without importing either app's types.
 */
export type BusyCalendarLike = {
  calendarId: string;
  connectorId?: number;
  primary?: boolean;
  summary?: string | null;
  description?: string | null;
};

export type BusyConnectorLike = {
  id: number;
  provider: string;
  displayName: string;
  isLogin: boolean;
  supportsCalendar: boolean;
};

export type BusyCalendarGroup<C extends BusyCalendarLike> = {
  connectorId: number | undefined;
  /** The connector's own name, e.g. "kiran@bigbasinlabs.com". */
  label: string;
  provider: string;
  isLogin: boolean;
  calendars: C[];
};

export const sortBusyCalendars = <C extends BusyCalendarLike>(calendars: C[]): C[] =>
  [...calendars].sort((a, b) => {
    if (a.primary && !b.primary) return -1;
    if (!a.primary && b.primary) return 1;
    return (a.summary || a.calendarId).localeCompare(b.summary || b.calendarId);
  });

export const groupBusyCalendars = <C extends BusyCalendarLike>(
  calendars: C[],
  allConnectors: BusyConnectorLike[],
): BusyCalendarGroup<C>[] => {
  const connectors = allConnectors.filter((c) => c.supportsCalendar);

  const grouped = new Map<number | undefined, C[]>();
  for (const cal of calendars) {
    const existing = grouped.get(cal.connectorId);
    if (existing) existing.push(cal);
    else grouped.set(cal.connectorId, [cal]);
  }

  return [...grouped.entries()]
    .map(([connectorId, cals]) => {
      const connector = connectors.find((c) => c.id === connectorId);
      return {
        connectorId,
        label: connector?.displayName ?? "Unknown account",
        provider: connector?.provider ?? "google",
        isLogin: connector?.isLogin ?? false,
        calendars: sortBusyCalendars(cals),
      };
    })
    .sort((a, b) => (a.connectorId ?? 0) - (b.connectorId ?? 0));
};

/** "Default calendar" for primaries, the calendar's own description otherwise. */
export const busyCalendarSubtitle = (calendar: BusyCalendarLike): string => {
  if (calendar.primary) return "Default calendar";
  if (calendar.description) return calendar.description;
  return "Shared calendar";
};
